// /home/coder/project/frontend/src/pages/CorCurrenciesList.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Keep for consistency, might add actions later
import { useGetCurrencies } from '../../hooks/genericHooks'; // Import the specific hook for currencies
import StandardTable from '../../components/common/StandardTable';
import { Card, Input, Space, Row, Col, Typography, message } from 'antd';
import {
  SearchOutlined,
  DollarOutlined, // Icon for currencies
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { CorCurrencies } from '../../types'; // Import the CorCurrencies type definition

const { Title } = Typography;

// Interface for filters
interface Filters {
  search: string;
}

const CorCurrenciesList: React.FC = () => {
  const navigate = useNavigate(); // Keep for consistency
  const [filters, setFilters] = useState<Filters>({ search: '' });

  // --- Data Fetching ---
  // Use the generic hook to fetch currency data
  const { data: currenciesData, isLoading, isError, error } = useGetCurrencies();
  // Extract the actual list of currencies, default to empty array if data is not yet loaded or fetch failed
  const currencyList = currenciesData?.data || [];

  // --- Error Handling ---
  // Display an error message if the fetch fails
  if (isError) {
    console.error("Failed to fetch currencies:", error);
    message.error(`Could not load currency data: ${error?.message || 'Unknown error'}`);
    // Optionally return an error display component here
  }

  // --- Filtering ---
  // Filter the currency list based on the search input
  const filteredCurrencies = currencyList.filter((currency) => {
    // If search filter is active
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      // Define the fields to search within (adjust based on CorCurrencies type fields)
      const searchFields = [
        // highlight-next-line - Corrected from currency.code to currency.currency_code
        currency.currency_code?.toLowerCase() || '', // Search by currency code
        currency.name?.toLowerCase() || '',         // Search by currency name
        currency.symbol?.toLowerCase() || '',       // Search by currency symbol (if exists) - Uncomment if needed
      ];
      // Return false (exclude item) if none of the fields include the search term
      if (!searchFields.some(field => field.includes(searchTerm))) {
        return false;
      }
    }
    // If no search term or if the item matches, include it
    return true;
  });

  // --- Table Columns ---
  // Define the columns for the Ant Design Table based on CorCurrencies type
  const columns: ColumnsType<CorCurrencies> = [
    {
      title: 'Code',
      dataIndex: 'currency_code', // Correct dataIndex
      key: 'currency_code',
      // Sorter for the currency code column
      // highlight-next-line - Corrected from a.code/b.code to a.currency_code/b.currency_code
      sorter: (a, b) => (a.currency_code || '').localeCompare(b.currency_code || ''),
      width: 100, // Fixed width for the code column
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // Sorter for the name column
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      ellipsis: true, // Add ellipsis (...) if the name is too long
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol) => symbol || '-', // Display '-' if null or undefined
      // Sorter for the symbol column (if needed)
      sorter: (a, b) => (a.symbol || '').localeCompare(b.symbol || ''), // Correct sorter if uncommented
      width: 100, // Fixed width for the symbol column
      align: 'center',
    },
    // ... (rest of the columns)
  ];

   // --- Pagination Change Handler ---
  // const handlePaginationChange = (page: number, newPageSize?: number) => {
  //   setCurrentPage(page);
  //   if (newPageSize && newPageSize !== pageSize) {
  //     setPageSize(newPageSize);
  //     // Optional: Reset to page 1 when page size changes
  //     // setCurrentPage(1);
  //   }
  // };

  // --- Render Component ---
  return (
    <Card>
      {/* Header Row: Title */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <DollarOutlined style={{ marginRight: 8 }} />
            Currency List
          </Title>
        </Col>
        {/* No "Add" button typically needed for a predefined list like currencies */}
      </Row>

      {/* Filter Row: Search Input */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Search Code, Name, or Symbol..."
            prefix={<SearchOutlined />}
            value={filters.search}
            // Update filter state on input change
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            allowClear // Show clear button (x)
            disabled={isLoading} // Disable input while data is loading
          />
        </Col>
      </Row>

      {/* Data Table */}
      <StandardTable
        dataSource={filteredCurrencies} // Data source is the filtered list
        columns={columns} // Columns definition
        rowKey="currency_code" // Use 'currency_code' as the unique key
        pagination={{ pageSize: 20 }}
        loading={isLoading} // Show loading indicator based on query state
        bordered // Add borders to table cells
        exportFileName="currencies"
      />
    </Card>
  );
};

export default CorCurrenciesList;
